import { useEffect } from 'react';
import { useQuery } from 'react-query';
import { useParams, useLocation } from 'react-router-dom';
import { getCategoryPageData } from '../../../section/CMS/api/cmsApi';
import PageLayout from '../layout/PageLayout';
import TextHeader from '../../textHeader/TextHeader';
import LoadingScreen from '../loading/LoadingScreen';
import MissingPage from '../errorMessages/MissingPage';
import { capitalizeString } from '../../../utilities/TextManipulation';
import { captureAnalytics } from '../../../utilities/Analytics';
import { CMSPageData } from '../../../section/CMS/types/CMSTypes';

// Import new CMS components
import CMSConsumerCarouselBanner from '../../../section/CMS/components/banner/CMSConsumerCarouselBanner';
import CMSConsumerFeaturedGallery from '../../../section/CMS/components/gallery/CMSConsumerFeaturedGallery';
import CMSConsumerCollectionsGrid from '../../../section/CMS/components/grid/CMSConsumerCollectionsGrid';

function BrandsTemplate() {
  const { item_group, brand } = useParams();
  const location = useLocation();

  // CMS Data Query - Consumer always uses live mode, searches by brand name with brand preference
  const {
    isSuccess: cmsSuccess,
    isError: cmsError,
    isLoading: cmsLoading,
    data: cmsResponse,
    refetch: cmsRefetch
  } = useQuery(
    [`cms-consumer-brand-${brand}`],
    () => getCategoryPageData(brand || '', 'live', 'brand'), // Always live mode for consumer, prefer brand type
    {
      staleTime: 600000, // Cache data for 10 minutes
      enabled: !!brand, // Only run if brand exists
    }
  );

  // Get the CMS data (always use live data for consumer)
  const cmsData: CMSPageData | null = cmsResponse?.cms_page || null;

  // Get the collections data for this brand - always use active data for consumer
  const collectionsData = cmsData?.direct_collections?.active || [];

  // Scroll management
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [brand]);

  // Analytics tracking
  useEffect(() => {
    if (cmsSuccess && cmsData && brand) {
      const title = cmsData.page_title || `brand-${brand}`;
      captureAnalytics('pageview', location.pathname, title);
    }
  }, [location.pathname, brand, cmsSuccess, cmsData]);

  // Determine loading and error states
  const isLoading = cmsLoading;
  const isError = cmsError;
  const isSuccess = cmsSuccess;

  if (!brand) {
    return <MissingPage />;
  }

  return (
    <>
      {isLoading && <LoadingScreen />}
      {isError && <MissingPage />}
      {isSuccess && (
        <>
          {/* CMS Consumer Carousel Banner */}
          {cmsData && (
            <CMSConsumerCarouselBanner
              bannerMediaOption={cmsData.consumer_banner_media_option}
              bannerMediaCarousel={cmsData.consumer_banner_media_carousel || []}
              bannerMediaVideo={cmsData.consumer_banner_media_video}
              bannerMediaVideoLink={cmsData.consumer_banner_media_video_link}
              bannerLinkCategoryId={cmsData.consumer_banner_link_category_id}
              bannerWidth={cmsData.consumer_banner_width}
              bannerHeight={cmsData.consumer_banner_height}
              viewMode="live"
            />
          )}

          {/* Text Header */}
          <TextHeader
            heading={cmsData?.page_title || capitalizeString(brand)}
            content={cmsData?.description || ''}
            isCentered={false}
            width='70%'
            marginTop={10}
            marginBottom={5}
          />


          {/* CMS Consumer Featured Gallery - Only show if enabled and has data */}
          {cmsData?.featured_collection_display_switch && cmsData?.featured_collection && (
            <CMSConsumerFeaturedGallery
              featuredCollection={cmsData.featured_collection}
              hasVideo={false}
              invert={false}
              marginTop={5}
              marginBottom={5}
              viewMode="live"
            />
          )}

          {/* CMS Consumer Collections Grid - Show collections for this brand */}
          {collectionsData.length > 0 && (
            <CMSConsumerCollectionsGrid
              collectionsData={collectionsData}
              applicationName={brand}
              marginTop={5}
              marginBottom={5}
              viewMode="live"
            />
          )}
        </>
      )}
    </>
  );
}

export default BrandsTemplate;